import { round } from 'lodash';
import {
  AppAutocomplete,
  AppDatePicker,
  AppSelectBox,
  AppTextInput,
  FieldRow,
} from 'components/FormElements';
import {
  Alert,
  ButtonGroup,
  Grid,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import PageContainer from 'components/PageContainer';
import { useEffect, useState } from 'react';
import AdviserSelect, { defaultLabelRenderer } from 'components/AdviserSelect';
import { paymentEmailOptions } from 'further-ui/labels';
import InvestorSearch from 'components/InvestorSearch';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useSortTranches } from 'hooks/ui/useSortTranches';
import { useFunds } from 'hooks/data/fund/useFunds';
import { useFirm } from 'hooks/data/firm/useFirm';
import { NumericFormat } from 'react-number-format';
import TextField from 'components/FormElements/AppTextInput';
import { useCreateInvestment } from 'hooks/data/investment/useInvestments';
import { Link, useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useInvestorCashBalanceById } from 'hooks/data/investor/useUploadedInvestorCashBalance';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { investmentFormSchema, isPaymentEmailStatusPaid } from './schema';
import { numberToCurrencyString } from 'further-ui/utils';
import { FirmBankDetailsSelector } from 'components/FirmBankDetailsSelector';
import PageContent from 'components/PageContent';
import RecordView from 'components/RecordView';
import Button from 'components/Button';
import { Row } from 'components/Layout';
import api from 'lib/trpcClient';
import { createInvestorEditRoute } from 'adminConstants/routes';
import { useSelectedInvestorWarnings } from './useSelectedInvestorWarnings';
import { useSubscriptionCodeWarning } from 'hooks/data/investment/useSubscriptionCodeWarning';
import { SortOrder } from 'further-types/utils';

const useStyles = makeStyles()((theme) => ({
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: 4,
  },
  link: {
    color: theme.palette.primary.main,
  },
}));

const fullCashBalancePaymentEmailOptions = paymentEmailOptions.map((item) => {
  if (isPaymentEmailStatusPaid(item.value)) {
    return item;
  }
  return { ...item, disabled: true };
});

const breadcrumbs = [
  { label: 'Dashboard' },
  { label: 'Subscriptions', link: '/investment' },
  { label: 'Create Subscription', link: '/', isActive: true },
];

const AddInvestment: React.FC = () => {
  const history = useHistory();
  const { classes } = useStyles();
  const { isSuperAdmin, firmId: userFirmId } = useGetRole();
  const investorCashBalance = useInvestorCashBalanceById();
  const theme = useTheme();

  const [firm, setFirm] = useState<{
    _id: string;
    domain: string;
    firmName: string;
  }>();
  const { tranches } = useFunds({
    firmId: userFirmId || firm?._id,
    leanResponse: true,
    refetchOnWindowFocus: true,
  });

  const sortedTranches = useSortTranches(tranches?.data);
  const { firms } = useFirm({ params: { firmId: userFirmId } });
  const [investorId, setInvestorId] = useState<string>();
  const [useCashBalance, setUseCashBalance] = useState(false);
  const [cashBalanceError, setCashBalanceError] = useState(false);
  const [currentInvestorCashBalance, setCurrentInvestorCashBalance] =
    useState(0);

  const { data: investorData } = api.investor.getInvestor.useQuery(
    { investorId: investorId ?? '' },
    { enabled: !!investorId, refetchOnWindowFocus: 'always' },
  );

  const investorWarnings = useSelectedInvestorWarnings({ investorData });

  const { mutate: createInvestment, isPending } = useCreateInvestment({
    onCreate: (id) => history.push(`/view-investment/${id}`),
  });

  useEffect(() => {
    if (!investorId || (isSuperAdmin && !firm?._id)) return;
    investorCashBalance.mutate({
      investorId,
      //@ts-expect-error
      firmId: firm?._id || userFirmId,
    });
  }, [firm, investorId]);

  useEffect(() => {
    setCurrentInvestorCashBalance(investorCashBalance?.data?.data || 0);
  }, [investorCashBalance.data]);

  const formMethods = useForm({
    resolver: zodResolver(investmentFormSchema),
  });
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = formMethods;

  const externalInvestmentId = watch('externalInvestmentId');
  const subscriptionCodeWarning = useSubscriptionCodeWarning({
    subscriptionCode: externalInvestmentId,
    firmId: isSuperAdmin ? firm?._id : userFirmId,
  });

  const defaultAdviserId = investorData?.defaultAdviserId;

  const adviser = watch('adviser');
  const defaulAdviserNotSet =
    !!defaultAdviserId && defaultAdviserId !== adviser?._id;

  useEffect(() => {
    if (!adviser && defaultAdviserId) {
      setValue('adviser', { _id: defaultAdviserId });
    }
  }, [investorData]);

  const onSubmit = (data) => {
    if (data?.cashBalance > currentInvestorCashBalance) {
      setCashBalanceError(true);
      return;
    }
    if (!data?.adviser) {
      data.adviserFee = undefined;
    }

    if (Object.keys(errors).length) {
      return;
    } else {
      createInvestment({
        ...data,
        domain: firm?.domain,
        adviserId: data.adviser?._id,
        adviser: undefined,
      });
    }
  };

  useEffect(() => {
    setValue('investmentDate', new Date());
    setValue('paymentDate', new Date());
    setValue('firmId', userFirmId);
  }, []);

  const fundId = watch('fundId');
  const getFund = api.fund.get.useQuery(
    { id: fundId ?? '' },
    { enabled: !!fundId },
  );
  const investmentAmount = formMethods.watch('investmentAmount');
  const adviserFee = formMethods.watch('adviserFee');
  const cashBalance = formMethods.watch('cashBalance');
  const fullCashBalancePayment =
    useCashBalance &&
    cashBalance >= round((investmentAmount ?? 0) + (adviserFee ?? 0), 2);

  const paymentEmailStatus = formMethods.watch('paymentEmailStatus');
  const useReconciledPayment =
    isPaymentEmailStatusPaid(paymentEmailStatus) && !fullCashBalancePayment;

  const { data: coreFeeTemplates } = api.coreFeeTemplates.list.useQuery(
    {
      firmId: userFirmId || firm?._id,
      order: SortOrder.Desc,
      orderBy: 'createdAt',
    },
    { refetchOnWindowFocus: 'always' },
  );

  const { data: performanceFeeTemplates } =
    api.performanceFeeTemplates.list.useQuery(
      {
        firmId: userFirmId || firm?._id,
        order: SortOrder.Desc,
        orderBy: 'createdAt',
      },
      { refetchOnWindowFocus: 'always' },
    );

  useEffect(() => {
    // On fund change, set the core and performance fee templates to the fund's default
    setValue('coreFeeTemplateId', getFund.data?.coreFeeTemplateId || '');
    setValue(
      'performanceFeeTemplateId',
      getFund.data?.performanceFeeTemplateId || '',
    );
  }, [getFund.data]);

  return (
    <PageContainer heading="Create Subscription" breadcrumbs={breadcrumbs}>
      <PageContent>
        <FormProvider {...formMethods}>
          <form>
            <RecordView>
              <FieldRow title="Firm*" centerTitle>
                {isSuperAdmin ? (
                  <Controller
                    name="firmId"
                    control={control}
                    render={({ fieldState: { error } }) => (
                      <AppAutocomplete
                        id="firmId"
                        options={firms.data?.result || []}
                        getOptionLabel={(option) => option?.firmName}
                        getOptionKey={(option) => option._id}
                        filterSelectedOptions
                        value={firms.data?.result?.find(
                          ({ _id }) => _id === firm?._id,
                        )}
                        onChange={(_, value) => {
                          setFirm({
                            //@ts-expect-error
                            _id: value?._id,
                            //@ts-expect-error
                            domain: value?.domain,
                            //@ts-expect-error
                            firmName: value?.firmName,
                          });
                          setValue('firmId', value?._id);
                          setValue('fundId', undefined);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Firm"
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                ) : (
                  <div>{firms?.data?.result[0]?.firmName}</div>
                )}
              </FieldRow>

              <FieldRow title="Investor*" centerTitle>
                <Controller
                  name="investorId"
                  control={control}
                  disabled={!userFirmId && !firm?._id}
                  render={({ field: { ...rest }, fieldState: { error } }) => {
                    const helperText = investorWarnings.hasErrors ? (
                      <Typography variant="body2">
                        This investor does not currently have a value populated
                        for{' '}
                        {Object.values(investorWarnings.errors).join(' or ')} on
                        their profile. Please add these details onto the
                        investor profile{' '}
                        <Link
                          className={classes.link}
                          to={createInvestorEditRoute(investorId ?? '')}
                        >
                          here
                        </Link>
                        .
                      </Typography>
                    ) : (
                      ''
                    );
                    return (
                      <InvestorSearch
                        {...rest}
                        onInvestorSelect={(value) => {
                          setValue('investorId', value);
                          setInvestorId(value);
                        }}
                        firmId={userFirmId ?? firm?._id}
                        error={!!error}
                        helperText={error?.message ?? helperText}
                      />
                    );
                  }}
                />
              </FieldRow>

              <FieldRow title="Tranche*" centerTitle>
                <Controller
                  name="fundId"
                  control={control}
                  render={({
                    fieldState: { error },
                    field: { value, name },
                  }) => (
                    <AppAutocomplete
                      key={firm?._id || 'no-firm'}
                      id="fundId"
                      name={name}
                      options={sortedTranches}
                      disabled={!userFirmId && !firm?._id}
                      getOptionLabel={(option) => option?.label}
                      getOptionKey={(option) => option._id}
                      value={value}
                      onChange={(_, value) => {
                        setValue('fundId', value?._id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Tranche"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Subscription date*" centerTitle>
                <Controller
                  name="investmentDate"
                  control={control}
                  render={({ field: { name, value } }) => (
                    <AppDatePicker
                      name={name}
                      value={value}
                      onChange={(value) => setValue('investmentDate', value)}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Subscription amount*" centerTitle>
                <Controller
                  name="investmentAmount"
                  control={control}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <NumericFormat
                      {...rest}
                      inputRef={ref}
                      required
                      placeholder="e.g. £5,000"
                      customInput={TextField}
                      prefix="£"
                      type="text"
                      decimalScale={2}
                      allowNegative={false}
                      allowLeadingZeros={false}
                      onChange={(event) =>
                        setValue(
                          'investmentAmount',
                          Number(
                            event.target.value
                              .replace('£', '')
                              .replaceAll(/,/g, ''),
                          ),
                        )
                      }
                      thousandSeparator
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Adviser" centerTitle>
                <Controller
                  name="adviser"
                  control={control}
                  render={({ field: { value } }) => (
                    <Grid container>
                      <Grid size={{ sm: 12, md: defaulAdviserNotSet ? 8 : 12 }}>
                        <AdviserSelect
                          adviserId={value?._id}
                          onChange={(adviser) => {
                            setValue('adviser', adviser || undefined);
                          }}
                          placeholder="N/A - Direct subscription"
                          labelRenderer={(option) =>
                            option ? defaultLabelRenderer(option) : 'Direct'
                          }
                          id="adviserId"
                          refetchOnWindowFocus={true}
                        />
                      </Grid>
                      {defaulAdviserNotSet && (
                        <Grid size={{ sm: 12, md: 4 }}>
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => {
                              setValue('adviser', { _id: defaultAdviserId });
                            }}
                          >
                            Set to default adviser
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  )}
                />
                {defaulAdviserNotSet && (
                  <Alert severity="warning" sx={{ mt: 5 }}>
                    Changing the adviser will remove the selected default
                    adviser for this investor.
                  </Alert>
                )}
              </FieldRow>

              {adviser && (
                <FieldRow title="Advice Fee" centerTitle>
                  <Controller
                    name="adviserFee"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <NumericFormat
                        {...rest}
                        inputRef={ref}
                        required
                        placeholder="e.g. £5,000"
                        customInput={TextField}
                        prefix="£"
                        type="text"
                        decimalScale={2}
                        allowNegative={false}
                        allowLeadingZeros={false}
                        onChange={(event) =>
                          setValue(
                            'adviserFee',
                            Number(
                              event.target.value
                                .replace('£', '')
                                .replaceAll(/,/g, ''),
                            ),
                          )
                        }
                        thousandSeparator
                      />
                    )}
                  />
                </FieldRow>
              )}

              <FieldRow title="External subscription ID" topTitle>
                <Controller
                  name="externalInvestmentId"
                  control={control}
                  render={({ field: { name, value } }) => (
                    <>
                      <AppTextInput
                        name={name}
                        variant="outlined"
                        style={{ maxWidth: 300 }}
                        value={value}
                        onChange={(event) =>
                          setValue('externalInvestmentId', event.target.value)
                        }
                      />
                      {subscriptionCodeWarning.hasWarning && (
                        <Alert severity="warning" sx={{ mt: 5 }}>
                          {subscriptionCodeWarning.message}
                        </Alert>
                      )}
                    </>
                  )}
                />
              </FieldRow>

              <FieldRow title="Core fee preset" centerTitle>
                <Controller
                  name="coreFeeTemplateId"
                  control={control}
                  render={({ field: { value } }) => (
                    <AppSelectBox
                      data={[
                        {
                          _id: '',
                          name: 'No fees',
                        },
                        ...(coreFeeTemplates?.result || []),
                      ]}
                      valueKey="_id"
                      labelKey="name"
                      value={value}
                      onChange={(event) =>
                        setValue(
                          'coreFeeTemplateId',
                          event.target.value as string,
                        )
                      }
                      disabled={coreFeeTemplates?.result?.length === 0}
                      backgroundColor={
                        coreFeeTemplates?.result?.length === 0
                          ? theme.palette.grey[100]
                          : undefined
                      }
                      maxWidth={300}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Performance fee preset" centerTitle>
                <Controller
                  name="performanceFeeTemplateId"
                  control={control}
                  render={({ field: { value } }) => (
                    <AppSelectBox
                      data={[
                        {
                          _id: '',
                          name: 'No fees',
                        },
                        ...(performanceFeeTemplates?.result || []),
                      ]}
                      valueKey="_id"
                      labelKey="name"
                      value={value}
                      onChange={(event) =>
                        setValue('performanceFeeTemplateId', event.target.value)
                      }
                      disabled={performanceFeeTemplates?.result?.length === 0}
                      backgroundColor={
                        performanceFeeTemplates?.result?.length === 0
                          ? theme.palette.grey[100]
                          : undefined
                      }
                      maxWidth={300}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Use cash balance" centerTitle>
                <Controller
                  name="useCashBalance"
                  control={control}
                  render={({ field: { name, value } }) => (
                    <Row centered spacing="xs">
                      <span>No</span>
                      <Switch
                        name={name}
                        checked={value}
                        inputProps={{ 'aria-label': 'Switch' }}
                        onChange={(_, checked) => {
                          setValue(name, checked);
                          setUseCashBalance(checked);
                        }}
                      />
                      <span>Yes</span>
                    </Row>
                  )}
                />
              </FieldRow>

              {useCashBalance && (
                <>
                  <FieldRow title="Current cash balance" centerTitle>
                    <p>{numberToCurrencyString(currentInvestorCashBalance)}</p>
                  </FieldRow>

                  <FieldRow title="Amount to use from cash balance" centerTitle>
                    <Controller
                      name="cashBalance"
                      control={control}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <NumericFormat
                          {...rest}
                          inputRef={ref}
                          required
                          customInput={TextField}
                          placeholder="e.g. £100"
                          prefix="£"
                          type="text"
                          decimalScale={2}
                          onChange={(event) =>
                            setValue(
                              'cashBalance',
                              Number(
                                event.target.value
                                  .replace('£', '')
                                  .replace(/,/g, ''),
                              ),
                            )
                          }
                          thousandSeparator
                          allowNegative={false}
                          allowLeadingZeros={false}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                    {cashBalanceError && (
                      <div className={classes.errorText}>
                        Cash balance cannot exceed current cash balance
                      </div>
                    )}
                  </FieldRow>
                </>
              )}

              <FieldRow title="Payment and email status*" centerTitle>
                <Controller
                  name="paymentEmailStatus"
                  control={control}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <AppSelectBox
                      inputRef={ref}
                      {...rest}
                      required
                      data={
                        fullCashBalancePayment
                          ? fullCashBalancePaymentEmailOptions
                          : paymentEmailOptions
                      }
                      valueKey="value"
                      labelKey="label"
                      variant="outlined"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FieldRow>

              {useReconciledPayment && (
                <>
                  <FieldRow title="Date of payment" centerTitle>
                    <Controller
                      name="paymentDate"
                      control={control}
                      render={({
                        field: { name, value },
                        fieldState: { error },
                      }) => (
                        <AppDatePicker
                          name={name}
                          value={value}
                          onChange={(value) => setValue('paymentDate', value)}
                          error={!!error}
                          helperText={error?.message}
                          disableFuture
                        />
                      )}
                    />
                  </FieldRow>

                  <FieldRow title="Paid to" centerTitle>
                    <Controller
                      name="paidToFirmBankDetailId"
                      control={control}
                      render={({
                        field: { value, ...rest },
                        fieldState: { error },
                      }) => (
                        <FirmBankDetailsSelector
                          {...rest}
                          currentValue={value}
                          //@ts-expect-error
                          firmId={userFirmId || firm?._id}
                          placeholder="Select receipt account"
                          error={!!error}
                          helperText={error?.message}
                          refetchOnWindowFocus={true}
                        />
                      )}
                    />
                  </FieldRow>

                  <FieldRow title="Reference" centerTitle>
                    <Controller
                      name="reference"
                      control={control}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <AppTextInput
                          {...rest}
                          inputRef={ref}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </FieldRow>
                </>
              )}

              <ButtonGroup>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant="contained"
                  color="primary"
                  loading={isPending}
                >
                  Save
                </Button>
              </ButtonGroup>
            </RecordView>
          </form>
        </FormProvider>
      </PageContent>
    </PageContainer>
  );
};

export default AddInvestment;
